import { Button, Divider, Row, Col, Space, Table, message } from 'antd'
import { downloadFile } from 'Services/WeaverUtils'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { invoice } from './queries'
import moment from 'moment'
import { store } from '~/Store'
import { copyToClipboard, sorter } from './helpers'
import GenerateInvoice from '../../GenerateInvoice'
import ACLService from 'Services/ACL_Service'

const euroFormatter = new Intl.NumberFormat('en-NL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const renderEuroValue = (val) => isNaN(val) ? '-' : `€${euroFormatter.format(val)}`

const getColumns = (keyPrefix) => {
  return ['naam', 'datum', 'bruto', 'prestatieKorting', 'beschikbaarheidsCorrectie', 'netto']
  .map((key) => ({
    title: _.startCase(key),
    dataIndex: key,
    key: `${keyPrefix}-${key}`,
    sorter: (a, b) => sorter(a[key], b[key])
  }))
}

const OverallInvoice = ({ ...props }) => {
  const { spaceId } = useParams();
  const { isError, error, data, isLoading } = useQuery(
    [`${spaceId}.invoice.dashboard.overall`],
    async () => {
      const res = await new Weaver.Query(undefined, 'afsluitingen').equalTo('name', 'Result').find()
      if (res.length > 0) {
        return res[0].attributes()
      } else {
        throw new Error('Unable to locate the Huidige Factuur result.')
      }
    }
  )

  const reduceOverallInvoiceData = ({
    beschikbaarheidsCorrectie,
    brutoBeschikbaarheidsVergoeding,
    name,
    nettoBeschikbaarheidsVergoeding,
    datum,
    prestatieKorting,
  }) => {
    return [
      {
        key: `overall-invoice-0`,
        naam: name,
        netto: renderEuroValue(nettoBeschikbaarheidsVergoeding),
        datum: moment(datum).format('YYYY-MM-DD'),
        bruto: renderEuroValue(brutoBeschikbaarheidsVergoeding),
        prestatieKorting: renderEuroValue(prestatieKorting),
        beschikbaarheidsCorrectie: renderEuroValue(beschikbaarheidsCorrectie)
      }
    ]
  }

  return (
    <>
      { isError ? message.info(error) : null }
      <Divider
        orientation="left"
        orientationMargin="0"
      >Huidige Factuur</Divider>
      <Table
        title={data ? copyToClipboard([data]) : () => null}
        loading={isLoading}
        bordered={true}
        columns={getColumns('OverallInvoice')}
        dataSource={data ? reduceOverallInvoiceData(data) : []}
        pagination={false}
        {...props }
      />
    </>
  )
}

const DownloadFile = ({ fileId, name }) => {

  const { spaceId } = useParams();
  const { data } = useQuery(
    [`${spaceId}.invoice.dashboard.${fileId}.download`],
    () => Weaver.File.list()
  )

  const fileExists = (files) => files.some((_file) => _file.id() === fileId)

  return (
    <Button
      shape="round"
      type="primary"
      style={{ width: '100%' }}
      onClick={() => downloadFile(fileId, `${name}.pdf`)}
      disabled={data ? !fileExists(data) : true}
    >
      Downloaden
    </Button>
  )
}

const QuarterlyInvoice = ({ quarter, ...props }) => {
  const { spaceId } = useParams();
  const { data, isLoading, refetch } = useQuery(
    [quarter, `${spaceId}.invoice.dashboard.${quarter}`],
    () => new Weaver.Sparql(invoice(quarter)).find() || false
  )

  const reduceQuarterlyInvoiceData = (nodeSets) => (
    nodeSets.naam.map((naam, i) => ({
      key: `quarterly-invoice-${i}`,
      naam: naam.value || "",
      netto: renderEuroValue(nodeSets.netto[i]?.value),
      fileId: nodeSets.fileId[i]?.value || "",
      datum: moment(nodeSets.datum[i]?.value).format('YYYY-MM-DD') || "",
      bruto: renderEuroValue(nodeSets.bruto[i]?.value),
      prestatieKorting: renderEuroValue(nodeSets.prestatieKorting[i]?.value),
      beschikbaarheidsCorrectie: renderEuroValue(nodeSets.beschikbaarheidsCorrectie[i]?.value)
    }))
  )


  const downloadFileColumn = {
    title: 'Bestand',
    dataIndex: '',
    key: 'fileId',
    render: ({ fileId, name }) => {
      return (
        <DownloadFile fileId={fileId} name={name} />
      )
    }
  }

  const renderInvoiceHeader = () => {
    return (
      <Divider
        orientation="left"
        orientationMargin="0"
      >
        Kwartaal Factuur
      </Divider>
    )
  }

  const renderGenerateInvoice = (qtr) => {
    return (
      <Row>
        <Col span={12}>
          { renderInvoiceHeader() }
        </Col>
        <Col span={12}>
          <Divider
            orientation="right"
            orientationMargin="0"
          >
            <GenerateInvoice quarter={qtr} refetch={refetch} />
          </Divider>
        </Col>
      </Row>
    )
  }

  return (
    <>
      { ACLService.hasWriteAccess() ? renderGenerateInvoice(quarter) : renderInvoiceHeader() }
      <Table
        title={data ? copyToClipboard(reduceQuarterlyInvoiceData(data)) : () => null}
        pagination={{position: ["topRight", "none"]}}
        loading={isLoading}
        bordered={true}
        columns={[...getColumns('QuarterlyInvoice'), downloadFileColumn ]}
        dataSource={data ? reduceQuarterlyInvoiceData(data) : []}
        { ...props }
      />
    </>
  )
}

const Invoice = ({ quarter }) => {

  const isMonteur = store.getState().user.isMonteur

  return (
    <Space
      direction="vertical"
      style={{ width: '100%' }}
      size={50}
    >
      <OverallInvoice/>
      { isMonteur ? null : <QuarterlyInvoice quarter={quarter} /> }
    </Space>
  )
}

Invoice.title = "Factuur"

export default Invoice
